import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { showToast } from "../primitives";
import {
  useWrappedApiHealth,
  useResetCircuitBreaker,
  useUpdateWrappedApi,
} from "../../hooks/use-wrapped-apis";

/**
 * Sprint 16b — live health for a wrapped API. Polls get_wrapped_api_health
 * every 10s: circuit-breaker state, the caller's rate-limit headroom, success
 * rate + p50/p95 latency over the last hour. Owners can reset an open breaker
 * and adjust the per-minute rate limit inline.
 */
export function HealthIndicator({
  slug,
  rateLimitPerMin,
  isOwner,
}: {
  slug: string;
  rateLimitPerMin: number;
  isOwner: boolean;
}) {
  const health = useWrappedApiHealth(slug);
  const resetBreaker = useResetCircuitBreaker();
  const update = useUpdateWrappedApi();

  const [rl, setRl] = useState(String(rateLimitPerMin));
  const [editingRl, setEditingRl] = useState(false);
  useEffect(() => setRl(String(rateLimitPerMin)), [rateLimitPerMin]);

  const h = health.data;
  const breaker = h?.circuit_breaker_state ?? "closed";
  const successPct = h && h.success_rate_last_hour !== null ? Math.round(h.success_rate_last_hour * 100) : null;
  const rlPct = h ? Math.round((h.rate_limit_remaining / Math.max(1, h.rate_limit_per_min)) * 100) : 100;

  async function handleReset() {
    try {
      await resetBreaker.mutateAsync(slug);
      showToast("Circuit breaker reset");
      health.refetch();
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Couldn't reset breaker", "error", true);
    }
  }

  async function saveRateLimit() {
    const n = Number(rl);
    if (!Number.isFinite(n) || n <= 0) {
      showToast("Rate limit must be a positive number.", "error", true);
      return;
    }
    try {
      await update.mutateAsync({ slug, rate_limit_per_min: n });
      showToast("Rate limit updated");
      setEditingRl(false);
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Couldn't update rate limit", "error", true);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <Stat label="Breaker">
          <span style={breakerBadge(breaker)}>
            <i
              className={`ti ${breaker === "closed" ? "ti-circle-check" : breaker === "open" ? "ti-alert-triangle" : "ti-loader"}`}
              style={{ fontSize: 11 }}
              aria-hidden="true"
            />
            {breaker}
          </span>
        </Stat>
        <Stat label="Success (1h)">
          <span style={{ fontSize: 13, color: "var(--color-text-primary)" }}>
            {successPct === null ? "—" : `${successPct}%`}
          </span>
        </Stat>
        <Stat label="p50 / p95">
          <span style={{ fontSize: 13, color: "var(--color-text-primary)" }}>
            {h?.p50_latency_ms == null ? "—" : `${h.p50_latency_ms}ms`} / {h?.p95_latency_ms == null ? "—" : `${h.p95_latency_ms}ms`}
          </span>
        </Stat>
        <Stat label="Calls (1h)">
          <span style={{ fontSize: 13, color: "var(--color-text-primary)" }}>{h?.call_count_last_hour ?? "—"}</span>
        </Stat>
      </div>

      <div>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
          <span style={statLabel}>Your rate limit</span>
          <span style={{ fontSize: 11, color: "var(--color-text-tertiary)" }}>
            {h ? `${h.rate_limit_remaining} / ${h.rate_limit_per_min} per min` : "—"}
          </span>
        </div>
        <div style={barTrack}>
          <div style={{ ...barFill, width: `${Math.max(2, Math.min(100, rlPct))}%`, background: rlPct < 20 ? "var(--c-red-600, var(--color-text-info))" : "var(--color-text-info)" }} />
        </div>
      </div>

      {breaker === "open" && isOwner && (
        <button type="button" onClick={handleReset} disabled={resetBreaker.isPending} style={resetBtn}>
          <i className="ti ti-refresh" style={{ fontSize: 12 }} aria-hidden="true" /> Reset breaker
        </button>
      )}

      {isOwner && (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {editingRl ? (
            <>
              <input value={rl} onChange={(e) => setRl(e.target.value)} inputMode="numeric" style={rlInput} />
              <button type="button" onClick={saveRateLimit} disabled={update.isPending} style={miniPrimary}>Save</button>
              <button type="button" onClick={() => { setEditingRl(false); setRl(String(rateLimitPerMin)); }} style={miniGhost}>Cancel</button>
            </>
          ) : (
            <button type="button" onClick={() => setEditingRl(true)} style={miniGhost}>Adjust rate limit</button>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 64 }}>
      <span style={statLabel}>{label}</span>
      {children}
    </div>
  );
}

function breakerBadge(state: string): CSSProperties {
  const closed = { fg: "var(--c-green-800, var(--color-text-secondary))", bg: "var(--c-green-50, var(--color-background-secondary))" };
  const map: Record<string, { fg: string; bg: string }> = {
    closed,
    open: { fg: "var(--c-red-600, var(--color-text-primary))", bg: "var(--c-red-50, var(--color-background-secondary))" },
    half_open: { fg: "var(--c-amber-800, var(--color-text-secondary))", bg: "var(--c-amber-50, var(--color-background-secondary))" },
  };
  const c = map[state] ?? closed;
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 3,
    fontSize: 12,
    fontWeight: 500,
    color: c.fg,
    background: c.bg,
    borderRadius: "var(--border-radius-sm)",
    padding: "1px 7px",
  };
}

const statLabel: CSSProperties = { fontSize: 11, color: "var(--color-text-tertiary)" };

const barTrack: CSSProperties = {
  height: 6,
  borderRadius: 3,
  background: "var(--color-background-secondary)",
  overflow: "hidden",
};

const barFill: CSSProperties = { height: "100%", borderRadius: 3, transition: "width 0.2s" };

const resetBtn: CSSProperties = {
  fontSize: 12,
  padding: "5px 10px",
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  alignSelf: "flex-start",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-primary)",
  color: "var(--c-red-600, var(--color-text-primary))",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};

const rlInput: CSSProperties = {
  width: 70,
  boxSizing: "border-box",
  padding: "5px 8px",
  fontSize: 12,
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-primary)",
  fontFamily: "var(--font-sans)",
};

const miniPrimary: CSSProperties = {
  fontSize: 12,
  padding: "5px 10px",
  border: "none",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-text-primary)",
  color: "var(--color-background-primary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};

const miniGhost: CSSProperties = {
  fontSize: 12,
  padding: "5px 10px",
  border: "none",
  background: "none",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};
